/** @format */

import { Aggregation, Aggregations, ParentGroupSummary } from "@question-bank/shared";
import { useCart } from "../../contexts/cart/useCart";
import { ExamPreview } from "../../components/ExamPreview";
import { FilterPanel } from "../../components/FilterPanel";
import { SearchBar } from "../../components/SearchBar";
import { SearchResults } from "../../components/SearchResults";
import { searchQuestions } from "../../api/questions";
import { useTranslation } from "react-i18next";
import { useBrand } from "../../hooks/useBrand";
import { useSearch } from "../../hooks/useSearch";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useCallback, useEffect, useRef, useState } from "react";
import { Pagination } from "../../components/Pagination";
import { mergeAggregationCounts, mergeSpecificationCounts } from "../../utils/helpers";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { trackEvent, trackPageView } from "../../utils/analytics";
import { useLocation } from "react-router-dom";

const SUBJECT_PARAM = "s";
const LEVEL_PARAM = "l";
const TIER_PARAM = "t";
const SPECIFICATION_PARAM = "sp";
const SPEC_THEME_PARAM = "spth";
const SPEC_SUBTOPIC_PARAM = "spst";
const AO_PARAM = "ao";
const START_YEAR_PARAM = "sty";
const END_YEAR_PARAM = "eny";
const OFFSET_PARAM = "p";
const QUERY_PARAM = "q";
interface PublicFilters {
	q?: string;
	subject?: string;
	level?: string;
	tier?: string;
	specification?: string;
	specificationTheme?: string;
	specificationSubTopic?: string;
	assessmentObjective?: string;
	fromYear?: number;
	toYear?: number;
	offset?: number;
}

const parseNumberParam = (raw: string | null): number | undefined => {
	if (!raw) {
		return undefined;
	}
	const n = Number.parseInt(raw, 10);
	return Number.isNaN(n) ? undefined : n;
};

const readFiltersFromUrl = (): PublicFilters => {
	if (globalThis.location === undefined) {
		return {};
	}
	const params = new URL(globalThis.location.href).searchParams;
	return {
		q: params.get(QUERY_PARAM) ?? undefined,
		subject: params.get(SUBJECT_PARAM) ?? undefined,
		level: params.get(LEVEL_PARAM) ?? undefined,
		tier: params.get(TIER_PARAM) ?? undefined,
		specification: params.get(SPECIFICATION_PARAM) ?? undefined,
		specificationTheme: params.get(SPEC_THEME_PARAM) ?? undefined,
		specificationSubTopic: params.get(SPEC_SUBTOPIC_PARAM) ?? undefined,
		assessmentObjective: params.get(AO_PARAM) ?? undefined,
		fromYear: parseNumberParam(params.get(START_YEAR_PARAM)),
		toYear: parseNumberParam(params.get(END_YEAR_PARAM)),
		offset: parseNumberParam(params.get(OFFSET_PARAM)),
	};
};

const writeFiltersToUrl = (filters: PublicFilters) => {
	if (globalThis.location === undefined) {
		return;
	}
	const url = new URL(globalThis.location.href);
	const entries: [string, string | undefined][] = [
		[QUERY_PARAM, filters.q],
		[SUBJECT_PARAM, filters.subject],
		[LEVEL_PARAM, filters.level],
		[TIER_PARAM, filters.tier],
		[SPECIFICATION_PARAM, filters.specification],
		[SPEC_THEME_PARAM, filters.specificationTheme],
		[SPEC_SUBTOPIC_PARAM, filters.specificationSubTopic],
		[AO_PARAM, filters.assessmentObjective],
		[START_YEAR_PARAM, filters.fromYear === undefined ? undefined : String(filters.fromYear)],
		[END_YEAR_PARAM, filters.toYear === undefined ? undefined : String(filters.toYear)],
		[OFFSET_PARAM, filters.offset === undefined ? undefined : String(filters.offset)],
	];
	for (const [key, value] of entries) {
		if (value) {
			url.searchParams.set(key, value);
		} else {
			url.searchParams.delete(key);
		}
	}
	globalThis.history.replaceState(null, "", url.toString());
};

export const PublicSearchPage = () => {
	const { t } = useTranslation();
	const { brand } = useBrand();
	const { results, isLoading, error, search, reset } = useSearch(brand);
	const [filters, setFilters] = useState<PublicFilters>(() => readFiltersFromUrl());
	const [baselineAggregations, setBaselineAggregations] = useState<Aggregations | undefined>(undefined);
	const [subjectOptions, setSubjectOptions] = useState<Aggregation[] | undefined>(undefined);
	const { addedQuestions, addWholeQuestion, removeQuestion, clearAll, entryFor } = useCart();
	const [isOpen, setIsOpen] = useState(false);
	const [isPending, setIsPending] = useState(false);
	const isBusy = isPending || isLoading;
	const location = useLocation();

	useDocumentTitle(t("pageTitles.search"));

	// Filters are written to the URL via `history.replaceState`, which bypasses
	// React Router, so `usePageTracking` never sees them. Fire a `page_view` for
	// each filtered URL here (skipping the first render, which the router-level
	// tracker already reported).
	const isInitialRender = useRef(true);

	useEffect(() => {
		setIsPending(false);
		search(filters);
		writeFiltersToUrl(filters);
		if (isInitialRender.current) {
			isInitialRender.current = false;
		} else if (globalThis.location !== undefined) {
			trackPageView(globalThis.location.pathname + globalThis.location.search);
		}
	}, [filters, search]);

	// `filters` is already seeded from the URL on mount, so only re-read it when
	// the router navigates (e.g. a keyword submitted from the search bar).
	const isInitialUrlSync = useRef(true);
	useEffect(() => {
		if (isInitialUrlSync.current) {
			isInitialUrlSync.current = false;
			return;
		}
		setFilters(readFiltersFromUrl());
	}, [location.search]);

	useEffect(() => {
		let isCancelled = false;
		searchQuestions(brand, { subject: filters.subject })
			.then((response) => {
				if (!isCancelled) {
					setBaselineAggregations(response.aggregations);
				}
			})
			.catch(() => {
				// A failed baseline read must not crash the page or spam uncaught
				// rejections — clear the facets and let the main results surface any
				// error state.
				if (!isCancelled) {
					setBaselineAggregations(undefined);
				}
			});
		return () => {
			isCancelled = true;
		};
	}, [brand, filters.subject]);

	// Subject is single-select, so the dropdown must always list every subject
	// with its total count regardless of the current selection. Fetch the full,
	// unfiltered subject aggregation once per brand rather than reading it from
	// the (subject-filtered) main search results.
	useEffect(() => {
		let isCancelled = false;
		searchQuestions(brand, {})
			.then((response) => {
				if (!isCancelled) {
					setSubjectOptions(response.aggregations?.subject);
				}
			})
			.catch(() => {
				// Subject options are non-critical chrome; on failure leave the
				// dropdown empty rather than throwing an uncaught rejection.
				if (!isCancelled) {
					setSubjectOptions(undefined);
				}
			});
		return () => {
			isCancelled = true;
		};
	}, [brand]);

	const handleSubjectChange = useCallback((subject?: string) => {
		trackEvent("apply_filter", { filter_type: "subject", value: subject });
		setFilters((prev) => ({ ...prev, subject, offset: undefined }));
	}, []);

	const handleLevelChange = useCallback((level?: string) => {
		trackEvent("apply_filter", { filter_type: "level", value: level });
		setFilters((prev) => ({ ...prev, level, offset: undefined }));
	}, []);

	const handleTierChange = useCallback((tier?: string) => {
		trackEvent("apply_filter", { filter_type: "tier", value: tier });
		setFilters((prev) => ({ ...prev, tier, offset: undefined }));
	}, []);

	const handleSpecificationChange = useCallback(
		(selection: { themes?: string; topics?: string; subTopics?: string }) => {
			trackEvent("apply_filter", {
				filter_type: "specification",
				value: selection.subTopics ?? selection.topics ?? selection.themes,
			});
			setFilters((prev) => ({
				...prev,
				specification: selection.topics,
				specificationTheme: selection.themes,
				specificationSubTopic: selection.subTopics,
				offset: undefined,
			}));
		},
		[],
	);

	const handleAssessmentObjectiveChange = useCallback((assessmentObjective?: string) => {
		trackEvent("apply_filter", { filter_type: "assessment_objective", value: assessmentObjective });
		setFilters((prev) => ({ ...prev, assessmentObjective, offset: undefined }));
	}, []);

	const handleYearRangeChange = useCallback((fromYear?: number, toYear?: number) => {
		trackEvent("apply_filter", { filter_type: "year_range", from_year: fromYear, to_year: toYear });
		setFilters((prev) => ({ ...prev, fromYear, toYear, offset: undefined }));
	}, []);

	const hasActiveFilters =
		filters.q ||
		filters.level ||
		filters.specification ||
		filters.specificationTheme ||
		filters.specificationSubTopic ||
		filters.tier ||
		filters.assessmentObjective ||
		filters.fromYear ||
		filters.toYear;

	const handleReset = useCallback(() => {
		setFilters({});
		reset();
	}, [reset]);

	const handleRemoveQuestion = useCallback(
		(question: ParentGroupSummary) => {
			const entry = entryFor(question.parentQuestionId);
			if (entry) {
				removeQuestion(entry);
			}
		},
		[entryFor, removeQuestion],
	);

	const handlePageChange = (newOffset: number) => {
		trackEvent("search_page_change", { offset: newOffset });
		setFilters((prev) => ({ ...prev, offset: newOffset }));
	};

	const handleResetFilters = useCallback(() => {
		trackEvent("reset_filters");
		setFilters((prev) => ({ subject: prev.subject }));
	}, []);

	const handleClearAll = () => {
		trackEvent("clear_paper");
		clearAll();
		setIsOpen(false);
	};

	const handleCancel = () => {
		setIsOpen(false);
	};

	const handleClear = () => {
		setIsOpen(true);
	};

	return (
		<div className={`flex flex-col ${addedQuestions.length > 0 ? "pb-18" : ""}`}>
			<div className="flex flex-col w-full px-4 pb-0">
				<div className="shrink-0 flex flex-col max-w-full gap-3 mb-5">
					<h1 className="text-4xl font-semibold" data-testid="search-page-heading">
						{t("searchPage.heading")}
					</h1>
					<p className="text-(--public-color-text-default) font-roboto">{t("searchPage.subHeading")}</p>
				</div>

				<div className="shrink-0 flex flex-col max-w-full py-5 px-6.25 rounded-[10px] gap-5 border-(--public-color-border-default) bg-(--public-color-filter-panel-bg)">
					<FilterPanel
						subjects={subjectOptions}
						levels={mergeAggregationCounts(baselineAggregations?.level, results?.aggregations?.level)}
						tiers={mergeAggregationCounts(baselineAggregations?.tier, results?.aggregations?.tier)}
						specifications={mergeSpecificationCounts(
							baselineAggregations?.specification,
							results?.aggregations?.specification,
						)}
						assessmentObjectives={mergeAggregationCounts(
							baselineAggregations?.assessmentObjective,
							results?.aggregations?.assessmentObjective,
						)}
						years={mergeAggregationCounts(baselineAggregations?.year, results?.aggregations?.year)}
						selectedSubject={filters.subject}
						selectedLevel={filters.level}
						selectedTier={filters.tier}
						selectedSpecification={filters.specification}
						selectedSpecificationTheme={filters.specificationTheme}
						selectedSpecificationSubTopic={filters.specificationSubTopic}
						selectedAssessmentObjective={filters.assessmentObjective}
						selectedFromYear={filters.fromYear}
						selectedToYear={filters.toYear}
						totalQuestions={isBusy ? undefined : results?.meta.total}
						onSubjectChange={handleSubjectChange}
						onLevelChange={handleLevelChange}
						onTierChange={handleTierChange}
						onSpecificationChange={handleSpecificationChange}
						onAssessmentObjectiveChange={handleAssessmentObjectiveChange}
						onYearRangeChange={handleYearRangeChange}
						onReset={handleResetFilters}
					/>

					<SearchBar shouldReset={filters.q === undefined} isSearching={isBusy} onPendingChange={setIsPending} />
				</div>
				<div className="w-full flex flex-col">
					{hasActiveFilters && !isBusy && (
						<div className="font-roboto font-semibold mt-2">
							{results && results.meta.total > 0 ? (
								<>
									<span className="font-bold text-[24px] font-lato mr-1">{results.meta.total}</span>
									<span className="font-lato font-normal text-[24px]">
										{t("searchPage.resultsCount", { count: results.meta.total })}
									</span>
								</>
							) : undefined}
						</div>
					)}

					<SearchResults
						results={results?.data}
						error={error}
						isLoading={isBusy}
						onRetry={() => search(filters)}
						onClearFilters={handleReset}
						onAddQuestion={addWholeQuestion}
						onRemoveQuestion={handleRemoveQuestion}
						addedQuestions={addedQuestions}
						pagination={
							results && results.meta.total > 0 ? (
								<Pagination
									total={results.meta.total}
									offset={results.meta.offset}
									limit={results.meta.limit}
									onPageChange={handlePageChange}
								/>
							) : undefined
						}
					/>
				</div>
			</div>

			{addedQuestions.length > 0 && (
				<ExamPreview addedQuestions={addedQuestions} onRemoveQuestion={removeQuestion} onClearAll={handleClear} />
			)}

			{isOpen && (
				<ConfirmationDialog
					onConfirm={handleClearAll}
					onCancel={handleCancel}
					title={t("confirmationDialog.title")}
					message={t("confirmationDialog.message")}
					btnDeleteMsg={t("confirmationDialog.clear")}
				/>
			)}
		</div>
	);
};
